import type { ComponentType } from 'react';
import {
  ArrowRightLeft,
  CalendarDays,
  CircleCheck,
  CircleX,
  Info,
  MailQuestion,
  Megaphone,
  MessageCircle,
  Save,
  Trophy,
  UserRoundPlus,
  type LucideProps,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useNotificationSettings } from '@/hooks/useNotificationSettings';
import type { NotificationEventType } from '@/types/notifications';
import { SettingsCard } from './SettingsCard';
import { useTranslation } from "react-i18next";

type NotificationEventDefinition = {
  key: NotificationEventType;
  icon: ComponentType<LucideProps>;
};

const NOTIFICATION_EVENTS: NotificationEventDefinition[] = [
  {
    key: 'lead.campaign_entered',
    icon: Megaphone,
  },
  {
    key: 'followup.handoff',
    icon: MailQuestion,
  },
  {
    key: 'lead.assigned',
    icon: UserRoundPlus,
  },
  {
    key: 'calendar.ai_created',
    icon: CalendarDays,
  },
  {
    key: 'opportunity.pipeline_changed',
    icon: ArrowRightLeft,
  },
  {
    key: 'opportunity.won',
    icon: Trophy,
  },
  {
    key: 'opportunity.lost',
    icon: CircleX,
  },
  {
    key: 'conversation.opened',
    icon: MessageCircle,
  },
  {
    key: 'conversation.closed',
    icon: CircleCheck,
  },
];

const NotificationsTab = () => {
  const { t } = useTranslation("settings");
  const {
    settings,
    loading,
    saving,
    isDirty,
    setWhatsappEnabled,
    setEventEnabled,
    save,
  } = useNotificationSettings();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#E5E7EB] border-t-[#2563EB] dark:border-[#3F3F46] dark:border-t-[#60A5FA]" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <p className="max-w-3xl text-sm text-[#64748B] dark:text-[#A1A1AA]">
          {t("notifications.intro")}
        </p>

        <Button
          type="button"
          onClick={() => void save()}
          disabled={!isDirty || saving}
          className="shrink-0 rounded-xl bg-[#2563EB] px-4 text-sm text-white shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8] disabled:opacity-50 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
        >
          <Save className="mr-2 h-4 w-4" />
          {saving ? t('notifications.saving') : t('notifications.saveChanges')}
        </Button>
      </div>

      <SettingsCard className="space-y-4 p-5">
        <div className="grid grid-cols-1 items-center gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)]">
          <div>
            <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              {t("notifications.channels")}
            </h3>
            <p className="mt-1 text-sm text-[#64748B] dark:text-[#A1A1AA]">
              {t("notifications.channelsHint")}
            </p>
          </div>

          <div className="flex items-center justify-between gap-4 rounded-xl border border-[#E5E7EB] bg-white p-4 dark:border-[#3F3F464D] dark:bg-[#18181B]">
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-green-50 text-[#16A34A] dark:bg-green-500/15 dark:text-green-400">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">{t("notifications.whatsapp")}</p>
                <p className="text-xs text-[#64748B] dark:text-[#A1A1AA]">
                  {t("notifications.whatsappHint")}
                </p>
              </div>
            </div>

            <Switch
              checked={settings.whatsappEnabled}
              onCheckedChange={setWhatsappEnabled}
              aria-label={t("notifications.whatsappAria")}
              activeColor="data-[state=checked]:bg-[#2563EB]"
            />
          </div>
        </div>

        <div className="flex items-start gap-2.5 rounded-xl border border-[#C9D8FF] bg-[#F5F8FF] px-3.5 py-3 text-sm font-medium text-[#2563EB] dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-[#60A5FA]">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {t("notifications.inAppNote")}
          </span>
        </div>
      </SettingsCard>

      <SettingsCard className="p-5">
        <div className="mb-4">
          <h3 className="text-base font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
            {t("notifications.rules")}
          </h3>
          <p className="mt-1 text-sm text-[#64748B] dark:text-[#A1A1AA]">
            {t("notifications.rulesHint")}
          </p>
        </div>

        <div className="overflow-hidden rounded-xl border border-[#E5E7EB] dark:border-[#3F3F464D]">
          <div className="grid grid-cols-[minmax(0,1fr)_88px] items-center border-b border-[#E5E7EB] bg-[#FBFCFE] px-4 py-2.5 text-[11px] font-bold uppercase tracking-wide text-[#64748B] dark:border-[#3F3F464D] dark:bg-[#18181B] dark:text-[#A1A1AA] sm:px-5">
            <span>{t("notifications.colNotification")}</span>
            <span className="text-center">{t("common.status")}</span>
          </div>

          {NOTIFICATION_EVENTS.map((event) => {
            const Icon = event.icon;

            return (
              <div
                key={event.key}
                className="grid grid-cols-[minmax(0,1fr)_88px] items-center border-b border-[#E5E7EB] px-4 py-3 transition-colors duration-200 last:border-b-0 hover:bg-[#F8FAFC] dark:border-[#3F3F464D] dark:hover:bg-[#3F3F46]/50 sm:px-5"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#EEF4FF] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
                      {t(`notifications.events.${event.key.replace(/\./g, "_")}.title`)}
                    </p>
                    <p className="text-xs text-[#64748B] dark:text-[#A1A1AA] sm:text-sm">
                      {t(`notifications.events.${event.key.replace(/\./g, "_")}.description`)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <Switch
                    checked={settings.events[event.key]}
                    onCheckedChange={(checked) => setEventEnabled(event.key, checked)}
                    aria-label={t("notifications.enableAria", { title: t(`notifications.events.${event.key.replace(/\./g, "_")}.title`) })}
                    activeColor="data-[state=checked]:bg-[#2563EB]"
                  />
                </div>
              </div>
            );
          })}
        </div>
      </SettingsCard>
    </div>
  );
};

export default NotificationsTab;
